import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  PlusCircleIcon,
  PhotoIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ClockIcon,
  InformationCircleIcon,
  ChartBarIcon,
  ArrowTrendingDownIcon,
  EyeSlashIcon,
  FireIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';

const auctionTypes = [
  {
    id: 'english',
    name: 'English',
    description: 'Subasta tradicional con pujas ascendentes',
    icon: ChartBarIcon,
    color: 'border-green-500 bg-green-500/10',
  },
  {
    id: 'dutch',
    name: 'Dutch',
    description: 'Precio descendente hasta primera compra',
    icon: ArrowTrendingDownIcon,
    color: 'border-orange-500 bg-orange-500/10',
  },
  {
    id: 'vickrey',
    name: 'Vickrey',
    description: 'Pujas selladas, ganador paga segunda puja',
    icon: EyeSlashIcon,
    color: 'border-purple-500 bg-purple-500/10',
  },
  {
    id: 'candle',
    name: 'Candle',
    description: 'Finalización aleatoria para evitar sniping',
    icon: FireIcon,
    color: 'border-yellow-500 bg-yellow-500/10',
  },
];

const categories = ['Art', 'Collectibles', 'Metaverse', 'Gaming', 'Music', 'Photography', 'Other'];
const durations = [
  { label: '1 día', value: 1 },
  { label: '3 días', value: 3 },
  { label: '5 días', value: 5 },
  { label: '7 días', value: 7 },
  { label: '14 días', value: 14 },
];

const CreateAuctionPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState({
    // NFT Info (blockchain data)
    contractAddress: '',
    tokenId: '',
    name: '',
    description: '',
    imageUrl: '',
    collectionName: '',
    category: '',
    image: null as File | null,
    imagePreview: '',
    // Auction Settings
    auctionType: 'english',
    startPrice: '',
    reservePrice: '',
    minBidIncrement: '5',
    duration: 3,
    // Dutch specific
    endPrice: '',
    decrementInterval: '1', // hours
    decrementAmount: '',
    // Candle specific
    windowStart: '80', // % of duration when random end can start
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({
        ...formData,
        image: file,
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Step 1: Import/register the NFT in the platform and get its internal UUID
      const importRes = await api.importNFT({
        contractAddress: formData.contractAddress,
        tokenId: formData.tokenId,
        name: formData.name,
        description: formData.description || undefined,
        image: formData.imageUrl,
        collectionName: formData.collectionName || formData.name,
      });

      const nftId = importRes.data?.id;
      if (!nftId) throw new Error('No se pudo registrar el NFT en la plataforma');

      // Step 2: Create the auction with the internal NFT UUID
      const startTime = new Date();
      const endTime = new Date();
      endTime.setDate(endTime.getDate() + formData.duration);

      await api.createAuction({
        nftId,
        type: formData.auctionType.toUpperCase(),
        startPrice: parseFloat(formData.startPrice),
        reservePrice: formData.reservePrice ? parseFloat(formData.reservePrice) : undefined,
        endPrice: formData.endPrice ? parseFloat(formData.endPrice) : undefined,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        minIncrement: formData.minBidIncrement ? parseFloat(formData.minBidIncrement) : undefined,
      });

      setSubmitSuccess(true);
      setTimeout(() => navigate('/dashboard/bids'), 2500);
    } catch (err: any) {
      setSubmitError(err?.message || 'Error al crear la subasta. Comprueba que estás conectado y el backend está activo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedAuctionType = auctionTypes.find(t => t.id === formData.auctionType);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <PlusCircleIcon className="w-8 h-8 text-primary-400" />
          Crear Subasta
        </h1>
        <p className="text-gray-400">Lista tu NFT para subasta en TEMPLUM</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-4 mb-8">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                step >= s
                  ? 'bg-primary-500 text-white'
                  : 'bg-dark-700 text-gray-500'
              }`}
            >
              {s}
            </div>
            {s < 3 && (
              <div className={`w-16 h-0.5 mx-2 ${
                step > s ? 'bg-primary-500' : 'bg-dark-700'
              }`} />
            )}
          </div>
        ))}
        <div className="ml-4 text-gray-400 text-sm">
          {step === 1 && 'Información del NFT'}
          {step === 2 && 'Configuración de Subasta'}
          {step === 3 && 'Revisión y Confirmación'}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Step 1: NFT Info */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <PhotoIcon className="w-6 h-6 text-primary-400" />
                Imagen del NFT
              </h2>

              <div className="flex gap-6">
                {/* Image Upload */}
                <div className="w-64 h-64 bg-dark-700 rounded-xl border-2 border-dashed border-dark-600 flex items-center justify-center relative overflow-hidden">
                  {formData.imagePreview ? (
                    <img
                      src={formData.imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center p-4">
                      <PhotoIcon className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">Arrastra o haz click</p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </div>

                {/* NFT Details */}
                <div className="flex-1 space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Contrato NFT (Contract Address) *</label>
                    <input
                      type="text"
                      value={formData.contractAddress}
                      onChange={(e) => setFormData({ ...formData, contractAddress: e.target.value })}
                      placeholder="0x..."
                      className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 font-mono text-sm"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">La dirección del contrato ERC-721 en Polygon</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Token ID *</label>
                      <input
                        type="text"
                        value={formData.tokenId}
                        onChange={(e) => setFormData({ ...formData, tokenId: e.target.value })}
                        placeholder="1234"
                        className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Nombre del NFT *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Ej: Equus Dualis II #7"
                        className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">URL de la imagen *</label>
                    <input
                      type="url"
                      value={formData.imageUrl}
                      onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                      placeholder="https://... o ipfs://..."
                      className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">URL de la imagen del NFT (IPFS, Arweave, HTTPS)</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Nombre de la Colección</label>
                    <input
                      type="text"
                      value={formData.collectionName}
                      onChange={(e) => setFormData({ ...formData, collectionName: e.target.value })}
                      placeholder="Ej: Equus Dualis II"
                      className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Categoría *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                      required
                    >
                      <option value="">Seleccionar categoría</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Descripción</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe tu NFT..."
                      rows={3}
                      className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 resize-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!formData.contractAddress || !formData.tokenId || !formData.name || !formData.imageUrl || !formData.category}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-dark-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                Siguiente
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Auction Settings */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Auction Type Selection */}
            <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <DocumentTextIcon className="w-6 h-6 text-primary-400" />
                Tipo de Subasta
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                {auctionTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = formData.auctionType === type.id;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, auctionType: type.id })}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        isSelected ? type.color : 'border-dark-600 hover:border-dark-500'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <Icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-gray-400'}`} />
                        <span className={`font-semibold ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                          {type.name}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm">{type.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Price Settings */}
            <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <CurrencyDollarIcon className="w-6 h-6 text-primary-400" />
                Configuración de Precios
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    {formData.auctionType === 'dutch' ? 'Precio Inicial (Alto) *' : 'Precio de Salida *'}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={formData.startPrice}
                      onChange={(e) => setFormData({ ...formData, startPrice: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 pr-16"
                      required
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">ETH</span>
                  </div>
                </div>

                {formData.auctionType === 'dutch' ? (
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Precio Final (Bajo) *</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={formData.endPrice}
                        onChange={(e) => setFormData({ ...formData, endPrice: e.target.value })}
                        placeholder="0.00"
                        step="0.01"
                        className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 pr-16"
                        required
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">ETH</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Precio de Reserva (Opcional)</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={formData.reservePrice}
                        onChange={(e) => setFormData({ ...formData, reservePrice: e.target.value })}
                        placeholder="0.00"
                        step="0.01"
                        className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 pr-16"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">ETH</span>
                    </div>
                  </div>
                )}

                {(formData.auctionType === 'english' || formData.auctionType === 'candle') && (
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Incremento Mínimo (%)</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={formData.minBidIncrement}
                        onChange={(e) => setFormData({ ...formData, minBidIncrement: e.target.value })}
                        placeholder="5"
                        min="1"
                        max="50"
                        step="1"
                        className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 pr-10"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Entre 1% y 50%</p>
                  </div>
                )}
              </div>
            </div>

            {/* Duration */}
            <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <ClockIcon className="w-6 h-6 text-primary-400" />
                Duración
              </h2>

              <div className="flex flex-wrap gap-3">
                {durations.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, duration: d.value })}
                    className={`px-6 py-3 rounded-lg transition-colors ${
                      formData.duration === d.value
                        ? 'bg-primary-500 text-white'
                        : 'bg-dark-700 text-gray-400 hover:text-white'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-6 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
              >
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                disabled={!formData.startPrice}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-dark-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                Siguiente
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Resumen de la Subasta</h2>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Preview */}
                <div>
                  {formData.imagePreview && (
                    <img
                      src={formData.imagePreview}
                      alt="Preview"
                      className="w-full aspect-square object-cover rounded-xl mb-4"
                    />
                  )}
                  <h3 className="text-xl font-bold text-white">{formData.name || 'Sin nombre'}</h3>
                  <p className="text-gray-400 text-sm mt-1">{formData.category}</p>
                  {formData.description && (
                    <p className="text-gray-500 text-sm mt-2">{formData.description}</p>
                  )}
                </div>

                {/* Details */}
                <div className="space-y-4">
                  <div className="bg-dark-900/50 rounded-lg p-4">
                    <p className="text-gray-500 text-sm mb-1">Tipo de Subasta</p>
                    <p className="text-white font-medium flex items-center gap-2">
                      {selectedAuctionType && <selectedAuctionType.icon className="w-5 h-5" />}
                      {selectedAuctionType?.name}
                    </p>
                  </div>

                  <div className="bg-dark-900/50 rounded-lg p-4">
                    <p className="text-gray-500 text-sm mb-1">Precio de Salida</p>
                    <p className="text-white font-bold text-xl">{formData.startPrice || '0'} ETH</p>
                  </div>

                  {formData.reservePrice && (
                    <div className="bg-dark-900/50 rounded-lg p-4">
                      <p className="text-gray-500 text-sm mb-1">Precio de Reserva</p>
                      <p className="text-white font-medium">{formData.reservePrice} ETH</p>
                    </div>
                  )}

                  <div className="bg-dark-900/50 rounded-lg p-4">
                    <p className="text-gray-500 text-sm mb-1">Duración</p>
                    <p className="text-white font-medium">{formData.duration} días</p>
                  </div>

                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <InformationCircleIcon className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-yellow-400 font-medium text-sm">Comisión de plataforma</p>
                        <p className="text-gray-400 text-sm">Se cobrará un 2.5% del precio final al vendedor</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {submitSuccess && (
              <div className="flex items-center gap-3 p-4 bg-green-500/20 border border-green-500/40 rounded-xl text-green-400">
                <CheckCircleIcon className="w-6 h-6 flex-shrink-0" />
                <p className="font-medium">¡Subasta creada con éxito! Redirigiendo a tus pujas...</p>
              </div>
            )}
            {submitError && (
              <div className="flex items-center gap-3 p-4 bg-red-500/20 border border-red-500/40 rounded-xl text-red-400">
                <ExclamationCircleIcon className="w-6 h-6 flex-shrink-0" />
                <p className="text-sm">{submitError}</p>
              </div>
            )}

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={isSubmitting || submitSuccess}
                className="px-6 py-3 bg-dark-700 hover:bg-dark-600 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                Anterior
              </button>
              <button
                type="submit"
                disabled={isSubmitting || submitSuccess}
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-dark-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                    </svg>
                    Creando...
                  </>
                ) : submitSuccess ? (
                  '¡Creada!'
                ) : (
                  'Crear Subasta'
                )}
              </button>
            </div>
          </motion.div>
        )}
      </form>
    </div>
  );
};

export default CreateAuctionPage;
